/** @jsxImportSource @emotion/react */

import { ApolloError, useMutation } from '@apollo/client'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { createTeamMember, createTeamMemberVariables } from '__generatedTypes__/createTeamMember'
import { findAllTeamMember_findAllTeamMember_items as TeamMember } from '__generatedTypes__/findAllTeamMember'
import { UpdateTeamMemberInput } from '__generatedTypes__/globalTypes'
import { removeTeamMember, removeTeamMemberVariables } from '__generatedTypes__/removeTeamMember'
import { updateTeamMember, updateTeamMemberVariables } from '__generatedTypes__/updateTeamMember'
import Pagination from 'components/Pagination'
import Table from 'components/Table'
import { CREATE_TEAM_MEMBER, DELETE_TEAM_MEMBER, UPDATE_TEAM_MEMBER } from 'queries/teamMember'
import { toast } from 'utils/toast'
import ManageTeamMemberModal, { DeletionError, handleServerErrors } from '../ManageTeamMemberModal'
import getTeamMemberColumns from '../TeamMemberColumns'
import { useTeamMembers } from '../useTeamMembers'
import * as classes from './styles'

export const handleDeletionError = (error: unknown) => {
  if (error instanceof Error) {
    if ('graphQLErrors' in error) {
      const gQLErr = error.graphQLErrors as ApolloError['graphQLErrors']

      return (
        gQLErr[0]?.extensions?.originalError || {
          message: 'Something went wrong',
        }
      )
    }
  }
}

const TeamMembersList: FC = () => {
  const { items, pagination, refetch } = useTeamMembers()
  const [deletionError, setDeletionError] = useState<DeletionError | null>(null)
  const [isTeamMemberModalOpen, setTeamMemberModalOpen] = useState<boolean>(false)
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | null>(null)
  const [updateTeamMember] = useMutation<updateTeamMember, updateTeamMemberVariables>(UPDATE_TEAM_MEMBER)
  const [createTeamMember] = useMutation<createTeamMember, createTeamMemberVariables>(CREATE_TEAM_MEMBER)
  const [removeTeamMember, { loading: removeLoading }] = useMutation<removeTeamMember, removeTeamMemberVariables>(
    DELETE_TEAM_MEMBER
  )

  useEffect(() => {
    refetch()
  }, [])

  useEffect(() => {
    setDeletionError(null)
  }, [selectedTeamMember])

  const teamMembersIndexed = useMemo(
    () =>
      items.map((item, index) => ({
        ...item,
        index: index + pagination.take * (pagination.page - 1),
      })),
    [items]
  )

  const handleEditClick = (member: TeamMember) => {
    setSelectedTeamMember(member)
    setTeamMemberModalOpen(true)
  }

  const handleModalClose = () => {
    setTeamMemberModalOpen(false)
    setSelectedTeamMember(null)
  }

  const handleTeamMemberUpdate = useCallback(
    async (teamMemberProps: UpdateTeamMemberInput) => {
      try {
        await updateTeamMember({
          variables: {
            params: {
              id: selectedTeamMember?.id,
              ...teamMemberProps,
            },
          },
        })
        refetch()
        handleModalClose()
        toast.success('Team member updated successfully')
      } catch (error) {
        toast.error('Failed to update team member')
        return handleServerErrors(error)
      }
    },
    [selectedTeamMember, refetch]
  )

  const handleTeamMemberCreate = useCallback(
    async (teamMemberProps: UpdateTeamMemberInput) => {
      try {
        await createTeamMember({
          variables: {
            params: { ...teamMemberProps, link: [] },
          },
        })
        refetch()
        handleModalClose()
        toast.success('Team member created successfully')
      } catch (error) {
        toast.error('Failed to create team member')
        return handleServerErrors(error)
      }
    },
    [refetch]
  )

  const handleTeamMemberDelete = useCallback(
    async (selectedTeamMemberId: string) => {
      try {
        await removeTeamMember({ variables: { params: selectedTeamMemberId } })
        refetch()
        handleModalClose()
      } catch (error) {
        const e = handleDeletionError(error)
        setDeletionError(e)
        toast.error(`Failed to delete team member, ${e.message}`)
      }
    },
    [refetch]
  )

  return (
    <div>
      <Table columns={getTeamMemberColumns(handleEditClick)} items={teamMembersIndexed || []} wrapperMinWidth={1142} />
      <Button
        css={classes.button}
        fullWidth
        size="large"
        color="blue"
        variant="outlined"
        onClick={() => setTeamMemberModalOpen(true)}
      >
        Add User <AddRoundedIcon />
      </Button>
      <Pagination
        css={classes.button}
        page={pagination.page}
        onPageChange={pagination.onChange}
        pageCount={pagination.count}
      />
      <Modal open={isTeamMemberModalOpen} onClose={handleModalClose}>
        <ManageTeamMemberModal
          onClose={handleModalClose}
          selectedTeamMember={selectedTeamMember}
          onTeamMemberDelete={handleTeamMemberDelete}
          onTeamMemberSubmit={selectedTeamMember ? handleTeamMemberUpdate : handleTeamMemberCreate}
          removeLoading={removeLoading}
          deletionError={deletionError}
        />
      </Modal>
    </div>
  )
}

export default TeamMembersList
